import { Controller } from "@hotwired/stimulus"

const ADDRESS_FINDER_SCRIPT_URL =
  "https://cdn.jsdelivr.net/npm/@ideal-postcodes/address-finder-bundled@4"

let addressFinderScriptPromise

// Connects to data-controller="ideal-postcodes-address"
export default class extends Controller {
  static targets = ["line1", "line2", "line3", "line4", "postcode"]
  static values = { apiKey: String }

  async connect() {
    this.connected = true
    if (!this.hasApiKeyValue || this.apiKeyValue.trim() === "") return

    try {
      await loadAddressFinderScript()
      if (!this.connected) return

      this.addressFinder = window.IdealPostcodes.AddressFinder.setup({
      apiKey: this.apiKeyValue,
      containerStyle: {
        flex: "1 1 auto",
        minWidth: "0",
        width: "1%",
        maxWidth: "none",
      },
      inputStyle: {
        width: "100%",
        maxWidth: "none",
      },
      outputFields: {
          line_1: this.line1Target,
          line_2: this.line2Target,
          line_3: this.line3Target,
          post_town: this.line4Target,
          postcode: this.postcodeTarget,
        },
        onAddressPopulated: () => this.markAddressAsApplied(),
      })
    } catch (error) {
      console.error("Ideal Postcodes Address Finder could not be loaded", error)
    }
  }

  disconnect() {
    this.connected = false
    this.addressFinder?.view?.detach()
    this.addressFinder = null
  }

  lookup(event) {
    event.preventDefault()
    this.line1Target.focus()
    if (this.line1Target.value.trim() === "") return

    const provenanceClasses = [
      "analysis-applied-field",
      "address-lookup-applied-field",
    ].filter((className) => this.line1Target.classList.contains(className))
    const title = this.line1Target.getAttribute("title")

    this.line1Target.dispatchEvent(new Event("input", { bubbles: true }))

    provenanceClasses.forEach((className) => this.line1Target.classList.add(className))
    if (title) this.line1Target.title = title
  }

  markAddressAsApplied() {
    this.squashAddressLines()

    this.addressTargets.forEach((field) => {
      field.dispatchEvent(new Event("input", { bubbles: true }))
      field.classList.add("address-lookup-applied-field")
      field.title = "Selected from address lookup"
    })
  }

  squashAddressLines() {
    const fields = [
      this.line1Target,
      this.line2Target,
      this.line3Target,
      this.line4Target,
    ]
    const values = fields
      .map((field) => field.value)
      .filter((value) => value.trim() !== "")

    fields.forEach((field, index) => {
      field.value = values[index] || ""
    })
  }

  get addressTargets() {
    return [
      this.line1Target,
      this.line2Target,
      this.line3Target,
      this.line4Target,
      this.postcodeTarget,
    ]
  }
}

function loadAddressFinderScript() {
  if (window.IdealPostcodes?.AddressFinder) return Promise.resolve()
  if (addressFinderScriptPromise) return addressFinderScriptPromise

  addressFinderScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = ADDRESS_FINDER_SCRIPT_URL
    script.async = true
    script.dataset.idealPostcodesAddressFinder = "true"
    script.addEventListener("load", resolve, { once: true })
    script.addEventListener("error", () => {
      addressFinderScriptPromise = null
      reject(new Error("Address Finder script failed to load"))
    }, { once: true })
    document.head.appendChild(script)
  })

  return addressFinderScriptPromise
}
